/**
 * Social Proof & Urgency — client-side randomization
 * Displays "X people viewing this" and "Only X left in stock"
 * with believable, traffic-calibrated values.
 */
class SocialProofUrgency extends HTMLElement {
  /** @type {Element | null} */
  viewerEl = null;
  /** @type {Element | null} */
  stockEl = null;
  /** @type {Element | null} */
  viewerRow = null;
  /** @type {Element | null} */
  stockRow = null;
  /** @type {number} */
  currentViewers = 0;
  /** @type {number} */
  currentStock = 0;
  /** @type {{ viewerMin: number, viewerMax: number, stockMin: number, stockMax: number, productId: string }} */
  cfg = { viewerMin: 1, viewerMax: 4, stockMin: 2, stockMax: 7, productId: '0' };
  /** @type {ReturnType<typeof setTimeout> | undefined} */
  _timer = undefined;

  connectedCallback() {
    this.viewerEl = this.querySelector('[data-viewer-count]');
    this.stockEl = this.querySelector('[data-stock-count]');
    this.viewerRow = this.querySelector('[data-viewer-row]');
    this.stockRow = this.querySelector('[data-stock-row]');

    this.cfg = {
      viewerMin: parseInt(this.dataset.viewerMin ?? '1') || 1,
      viewerMax: parseInt(this.dataset.viewerMax ?? '4') || 4,
      stockMin: parseInt(this.dataset.stockMin ?? '2') || 2,
      stockMax: parseInt(this.dataset.stockMax ?? '7') || 7,
      productId: this.dataset.productId ?? '0',
    };

    // Off-peak dampening: midnight–6 AM local time → cap viewers lower
    const hour = new Date().getHours();
    if (hour >= 0 && hour < 6) {
      this.cfg.viewerMax = Math.min(this.cfg.viewerMax, 2);
    }

    // Product-hour seed for consistency across quick reloads
    const seed = this.hashCode(this.cfg.productId + '-' + hour);

    // Initial values
    if (this.viewerEl) {
      this.currentViewers = this.seededRandom(seed, this.cfg.viewerMin, this.cfg.viewerMax);
      this.viewerEl.textContent = String(this.currentViewers);
    }

    if (this.stockEl) {
      this.currentStock = this.seededRandom(seed + 7, this.cfg.stockMin, this.cfg.stockMax);
      this.stockEl.textContent = String(this.currentStock);
    }

    // Fluctuate viewer count on a random interval (15–45s)
    if (this.viewerEl && this.viewerRow) {
      this.scheduleViewerUpdate();
    }
  }

  disconnectedCallback() {
    clearTimeout(this._timer);
  }

  scheduleViewerUpdate() {
    const delay = 15000 + Math.random() * 30000; // 15–45 seconds
    this._timer = setTimeout(() => {
      this.updateViewerCount();
      this.scheduleViewerUpdate();
    }, delay);
  }

  updateViewerCount() {
    const direction = Math.random() < 0.5 ? -1 : 1;
    /** @type {number} */
    let next = this.currentViewers + direction;
    next = Math.max(this.cfg.viewerMin, Math.min(this.cfg.viewerMax, next));
    if (next !== this.currentViewers) {
      this.currentViewers = next;
      if (this.viewerEl) {
        this.viewerEl.classList.add('sp-urgency__num--changing');
        this.viewerEl.textContent = String(next);
        setTimeout(() => {
          this.viewerEl?.classList.remove('sp-urgency__num--changing');
        }, 400);
      }
    }
  }

  /**
   * Simple deterministic hash → number
   * @param {string} str
   * @returns {number}
   */
  hashCode(str) {
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
      hash = ((hash << 5) - hash + str.charCodeAt(i)) | 0;
    }
    return Math.abs(hash);
  }

  /**
   * Seeded random integer in [min, max]
   * @param {number} seed
   * @param {number} min
   * @param {number} max
   * @returns {number}
   */
  seededRandom(seed, min, max) {
    const x = Math.sin(seed) * 10000;
    const normalized = x - Math.floor(x); // 0–1
    return Math.floor(normalized * (max - min + 1)) + min;
  }
}

customElements.define('social-proof-urgency', SocialProofUrgency);
